import { useSearchParams, Navigate } from 'react-router-dom'
import { Paragraph } from '@quickmd-llc/design-system'
import { useGetProvidersUserId } from '@/api/generated/doctor/doctor'
import { transformApiLicense, licenseToFormValues } from './transformLicense'
import { LicenseFormPage } from './LicenseFormPage'
import { flattenServiceTypes } from './flattenServiceTypes'
import { MEMBER_PAGE_ROUTE } from '@/utils/constants'
import type { LicenseFormValues } from './LicenseFormSchema'
import { useLicenseFormServiceTypes } from './useLicenseFormServiceTypes'
import { useEditLicense } from '../hooks/useEditLicense'

export function EditLicensePage() {
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('id') ?? ''
  const licenseId = searchParams.get('licenseId') ?? ''
  const editLicense = useEditLicense(userId, licenseId)

  const { data: provider, isLoading } = useGetProvidersUserId(userId)

  const serviceTypes = useLicenseFormServiceTypes()

  if (isLoading || !provider) {
    return <Paragraph>Loading...</Paragraph>
  }

  const licenses = provider.Licenses ?? []
  const rawLicense = licenses.find((l) => l?.LicenseId === licenseId)

  if (!rawLicense) {
    const tab = encodeURIComponent('Licenses & Productivity')
    return <Navigate to={`${MEMBER_PAGE_ROUTE}?id=${userId}&type=Provider&tab=${tab}`} replace />
  }

  const license = transformApiLicense(rawLicense)
  const formDefaults = licenseToFormValues(license)

  // Merge available visit types that aren't already in the license
  const flatTypes = flattenServiceTypes(serviceTypes ?? [])
  const allVisitTypes = flatTypes.map((ft) => {
    const existing = formDefaults.visitTypes.find((vt) => vt.visitTypeId === ft.visitTypeId)
    return existing ? { ...existing, serviceName: ft.serviceName, visitName: ft.visitName } : ft
  })

  // Existing licenses for duplicate check (exclude current)
  const existingLicenses = licenses

  async function handleSubmit(values: LicenseFormValues) {
    await editLicense.mutateAsync(values)
  }

  return (
    <LicenseFormPage
      mode='edit'
      defaultValues={{ ...formDefaults, visitTypes: allVisitTypes }}
      availableServiceTypes={serviceTypes ?? []}
      existingLicenses={existingLicenses}
      currentLicenseId={licenseId}
      successMessage='License updated!'
      onSubmit={handleSubmit}
      isPending={editLicense.isPending}
    />
  )
}
